/**
 * In-memory, user-scoped, TTL-based cache.
 *
 * Security contract:
 *
 *  1. ALL data lives in module-scope memory only — never in localStorage,
 *     sessionStorage, cookies, or any persistent/cross-origin storage.
 *     A tab close or page reload wipes the cache automatically.
 *
 *  2. Every cache key MUST be scoped to the authenticated user via
 *     userCacheKey(user, resource). A null key means "do not cache";
 *     every cacheGet/cacheSet call silently no-ops on null keys.
 *
 *  3. cacheClearAll() MUST be called on every logout and session-expiry
 *     code path. Using a full clear (rather than a user-scoped delete)
 *     ensures stale data from a previous user cannot bleed into a new
 *     session even when the previous user object is unavailable at clear time.
 *
 *  4. cacheClearAll() MUST also be called before storeAuth() (i.e. on login).
 *     Without this a fast user-switch within the same tab could briefly serve
 *     user A's data to user B before TTLs expire.
 *
 *  5. Never cache authentication tokens, raw credentials, or responses that
 *     embed them. Only cache UI-layer API responses (lists, status flags).
 *
 *  6. Mutations (create / update / delete) MUST either call cacheDelete() to
 *     force a fresh fetch on next access, or update the cache entry in-place.
 *     Silently leaving a stale cache entry after a write is a data integrity bug.
 */

/** TTL constants in milliseconds. Adjust per resource's sensitivity. */
export const TTL = {
  CONVERSATIONS:     30_000,  //  30 s — mutated frequently
  PROJECTS:          60_000,  //  60 s — mutated on CRUD
  KNOWLEDGE_GLOBAL: 600_000,  // 10 min — global graph is static within a session
  KNOWLEDGE_PROJECT: 300_000, //  5 min — project graph can change when docs are uploaded
  LIBRARY_DOCUMENTS: 60_000,  //  60 s — upload/delete/reprocess
  CVE_METRICS:      120_000,  //  2 min — sync-driven
  CVE_LIST:         120_000,  //  2 min
  ORGANIZATION:      60_000,  //  60 s — invites/members change infrequently
  USER_CONFIG:      300_000,  //  5 min — SAP environment settings
  MESSAGES:         300_000,  //  5 min — per-conversation message history
  BLOG:             600_000,  // 10 min — public blog list/posts
  UAT_BULK_RUNS:     30_000,  //  30 s — batch list
};

/** Hard cap on live entries. Prevents unbounded growth in long-lived tabs. */
const MAX_ENTRIES = 200;

const _store = new Map(); // key → { data: any, expiresAt: number }

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

/**
 * Build a user-scoped cache key.
 *
 * Returns null when the user identity cannot be determined — callers treat
 * null as "skip cache entirely" and fall through to the network.
 *
 * Uses the numeric DB id when available (most stable), otherwise falls back
 * to the email address. Both paths produce a string namespace that is
 * impossible to confuse with another user's namespace at the same length.
 */
export function userCacheKey(user, resource) {
  if (!user || !resource) return null;

  const namespace =
    user.id != null && String(user.id).length > 0
      ? String(user.id)
      : user.email || null;

  if (!namespace) return null;

  return `${namespace}:${resource}`;
}

// ---------------------------------------------------------------------------
// Core operations
// ---------------------------------------------------------------------------

/**
 * Retrieve a cached value.
 *
 * Returns null on: cache miss, expired entry, or null key.
 * Returns the cached data for a valid hit — including the value `false`,
 * which callers MUST NOT confuse with a miss (always check `!== null`).
 */
export function cacheGet(key) {
  if (!key) return null;

  const entry = _store.get(key);
  if (!entry) return null;

  if (Date.now() > entry.expiresAt) {
    _store.delete(key);
    return null;
  }

  return entry.data;
}

/**
 * Store a value under key with a TTL.
 *
 * No-ops silently on null key or non-positive TTL.
 * Evicts the oldest entry when the store reaches MAX_ENTRIES (Map insertion
 * order guarantees the first key() is the oldest live entry).
 */
export function cacheSet(key, data, ttlMs) {
  if (!key || ttlMs <= 0) return;

  if (_store.size >= MAX_ENTRIES) {
    const oldestKey = _store.keys().next().value;
    _store.delete(oldestKey);
  }

  _store.set(key, { data, expiresAt: Date.now() + ttlMs });
}

/** Delete a single cache entry. No-ops on null key. */
export function cacheDelete(key) {
  if (!key) return;
  _store.delete(key);
}

/**
 * Delete all entries whose key starts with prefix.
 * Use to bulk-invalidate all graph keys for a user on document upload, etc.
 */
export function cacheDeleteByPrefix(prefix) {
  if (!prefix) return;
  for (const key of _store.keys()) {
    if (key.startsWith(prefix)) _store.delete(key);
  }
}

/**
 * Purge every entry in the cache.
 *
 * This is the ONLY safe way to handle logout and login transitions.
 * Do NOT call a user-scoped delete here — the user object may not be
 * available (e.g. Supabase emits SIGNED_OUT before we can read the old user).
 */
export function cacheClearAll() {
  _store.clear();
}
